import type { AxiosRequestConfig } from 'axios';
import axiosInstance from './axiosInstance';

/** 객체의 모든 값 중 빈 문자열("")을 null로 변환 */
export function emptyStringToNull<T>(input: T): T {
  if (input === '') return null as T;

  if (Array.isArray(input)) {
    return input.map((item) => emptyStringToNull(item)) as T;
  }

  if (typeof input === 'object' && input !== null) {
    const result: any = {};
    for (const [key, value] of Object.entries(input)) {
      result[key] = emptyStringToNull(value);
    }
    return result;
  }

  return input;
}

const BASE_URL = import.meta.env.VITE_API_URL;

const parseApiError = (error: any) => {
  return {
    ...error,
    message: error?.message || 'An unknown error has occurred.',
  };
};

/** AxiosRequestConfig 확장: isThrottle 지원 */
declare module 'axios' {
  export interface AxiosRequestConfig {
    isThrottle?: boolean;
    snakeCaseParams?: boolean;
    emptyStringToNull?: boolean;
  }
}

/** 표준 API 응답 타입: { success, data, error } */
export type ApiResponse<T = any> = {
  success: boolean;
  data: T;
  error: { code: string; message: string } | null;
};

export const apiRequest = {
  get: async <T = any>(url: string, params?: any, config: AxiosRequestConfig = {}): Promise<ApiResponse<T>> => {
    try {
      const res = await axiosInstance.get<ApiResponse<T>>(`${config.baseURL ?? BASE_URL}${url}`, { params, snakeCaseParams: false, ...config });
      return res.data;
    } catch (e) {
      throw parseApiError(e);
    }
  },

  put: async <T = any>(url: string, data?: any, config: AxiosRequestConfig = { emptyStringToNull: false }): Promise<ApiResponse<T>> => {
    try {
      const processedData = config.emptyStringToNull ? emptyStringToNull(data) : data;
      const res = await axiosInstance.put<ApiResponse<T>>(`${config.baseURL ?? BASE_URL}${url}`, processedData, config);
      return res.data;
    } catch (e) {
      throw parseApiError(e);
    }
  },

  post: async <T = any>(url: string, data?: any, config: AxiosRequestConfig = { emptyStringToNull: false }): Promise<ApiResponse<T>> => {
    try {
      const isFormData = data instanceof FormData;
      const processedData = isFormData ? data : config.emptyStringToNull ? emptyStringToNull(data) : data;
      const res = await axiosInstance.post<ApiResponse<T>>(`${config.baseURL ?? BASE_URL}${url}`, processedData, config);
      return res.data;
    } catch (e) {
      throw parseApiError(e);
    }
  },

  delete: async <T = any>(url: string, data?: any, config: AxiosRequestConfig = { emptyStringToNull: false }): Promise<ApiResponse<T>> => {
    try {
      const processedData = config.emptyStringToNull ? emptyStringToNull(data) : data;
      const res = await axiosInstance.delete<ApiResponse<T>>(`${config.baseURL ?? BASE_URL}${url}`, { data: processedData, ...config });
      return res.data;
    } catch (e) {
      throw parseApiError(e);
    }
  },
};
